<template>
  <el-collapse-item :name="modelValue.Codigo">
    <template #title>
      <div>
        {{ modelValue.Descripcion }}
        <el-badge
          v-if="modelValue.ElementoProceso.length > 0"
          :value="modelValue.ElementoProceso.length"
          class="ml-2"
          type="primary"
        />
        <!-- Indicador de error de porcentaje en el título -->
        <el-badge
          v-if="porcentajeExcedido"
          value="!"
          class="ml-2"
          type="danger"
        />
      </div>
    </template>
    <div
      class="scrollable-container drop-zone"
      :data-zone-id="modelValue.Codigo"
      @dragover="handleDragOver"
      @drop="handleDrop"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
    >
      <template
        v-for="(proceso, indexProceso) in modelValue.ElementoProceso"
        :key="proceso.Codigo || `temp-${indexProceso}`"
      >
        <div
          v-if="
            dragOverIndex === indexProceso && draggedIndex !== null && draggedIndex !== indexProceso
          "
          class="drop-indicator"
        ></div>

        <div>
          <input-procesos-item
            :model-value="proceso"
            :procesos="procesos"
            :validar-porcentaje="debeValidarPorcentaje"
            :porcentaje-excedido="!porcentajeValido"
            :index="indexProceso"
            :zone-id="modelValue.Codigo"
            :codigo-elemento-tipo="elementoTipo"
            :modulo="currentModule"
            :procesos-del-elemento="procesosDelElemento"
            :codigo-familia-material="CodigoFamiliaMaterial"
            :class="{
              dragging: draggedIndex === indexProceso,
              'drag-over':
                dragOverIndex === indexProceso &&
                draggedIndex !== null &&
                draggedIndex !== indexProceso,
            }"
            @update:modelValue="
              (updatedProceso) => handleUpdateProceso(indexProceso, updatedProceso)
            "
            @delete-proceso="handleDeleteProceso(indexProceso, $event)"
            @drag-start="handleDragStart"
            @drag-end="handleDragEnd"
          />
        </div>
      </template>

      <div
        v-if="dragOverIndex === modelValue.ElementoProceso.length && draggedIndex !== null"
        class="drop-indicator drop-indicator-end"
      ></div>
    </div>
    <!-- Mensaje de advertencia cuando se excede el 100% -->
    <div
      v-if="porcentajeExcedido && currentElementoBase !== ElementoBase.EXTRUSION"
      class="error-message"
      style="margin-top: 10px; color: #f56c6c; font-size: 14px"
    >
      ⚠️ La suma de porcentajes de procesos supera el 100% (actual:
      {{ formatNumberLocale(totalPorcentaje, 2) }}%)
    </div>
    <div
      v-else-if="porcentajeIncompleto && currentElementoBase !== ElementoBase.EXTRUSION"
      class="error-message"
      style="margin-top: 10px; color: #f56c6c; font-size: 14px"
    >
      ⚠️ La suma de porcentajes de procesos es menor a 100% (actual:
      {{ formatNumberLocale(totalPorcentaje, 2) }}%)
    </div>
    <div class="button-position add-wrapper">
      <el-button
        class="add-button add-big"
        type="primary"
        size="default"
        :icon="IconAdd"
        @click="handleAddProceso"
        >Agregar</el-button
      >
    </div>
  </el-collapse-item>
</template>

<script setup>
import {
  onMounted,
  onUnmounted,
  ref,
  inject,
  computed,
  provide,
} from 'vue';
import IconAdd from '~icons/mdi/add';
import elemento_tipo_service from '@/services/elemento_tipo_service.js';
import InputProcesosItem from '@/shared/components/procesos/input_procesos_item.vue';
import { ElementoBase } from '@/enum/elemento_base';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';
import { generarUidProceso } from '@/shared/utils/procesos_precedentes';

const currentModule = inject('currentModule');
const currentElementoBase = inject('currentElementoBase', null);

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  elementoTipo: {
    type: Number,
    required: true,
  },
  CodigoFamiliaMaterial: {
    type: Number,
    required: false,
  },
  validarPorcentaje: { type: Boolean, default: false },
  // COTI-642: bajan desde input_procesos.vue (además del provide, como respaldo).
  procesosDelElemento: { type: Array, default: () => [] },
});

const procesos = ref([]);
const draggedIndex = ref(null);
const dragOverIndex = ref(null);
const draggedZoneId = ref(null);
const isDraggingGlobal = ref(false);
const TOLERANCIA_PORCENTAJE = 0.01;

const esProcesoMultiMaterial = (procesoWrapper) => {
  if (!procesoWrapper) return false;

  const proc = procesoWrapper.Proceso || {};
  if (proc.MultiMaterial === true) return true;

  const catsWrapper = Array.isArray(procesoWrapper.CategoriasMaterial)
    ? procesoWrapper.CategoriasMaterial
    : [];

  if (catsWrapper.some((c) => c?.MultiMaterial === true)) return true;

  const catsProc = Array.isArray(proc.CategoriasMaterial) ? proc.CategoriasMaterial : [];

  if (catsProc.some((c) => c?.MultiMaterial === true)) return true;

  const rels = Array.isArray(proc.ProcesoElementoTipoCategoriaMaterial)
    ? proc.ProcesoElementoTipoCategoriaMaterial
    : [];

  if (rels.some((rel) => rel && rel.MultiMaterial)) return true;

  return false;
};

const tieneAlgunoMultiMaterial = computed(() => {
  const procesos = Array.isArray(props.modelValue?.ElementoProceso)
    ? props.modelValue.ElementoProceso
    : [];

  return procesos.some((p) => esProcesoMultiMaterial(p));
});
// Proveer el estado global de dragging a todos los componentes hijos
provide('isDraggingGlobal', isDraggingGlobal);

// Computed para validar que la suma de porcentajes de procesos no exceda 100
const totalPorcentaje = computed(() => {
  if (!props.modelValue?.ElementoProceso) return 0;
  return props.modelValue.ElementoProceso.reduce((total, proceso) => {
    if (proceso.MarcadoParaEliminacion) return total;

    const v = Number(proceso?.Porcentaje ?? 0);
    return total + (Number.isFinite(v) ? v : 0);
  }, 0);
});
const diferenciaPorcentaje = computed(() => 100 - totalPorcentaje.value);

const debeValidarPorcentaje = computed(() => {
  return (
    props.validarPorcentaje &&
    tieneAlgunoMultiMaterial.value &&
    currentElementoBase !== ElementoBase.EXTRUSION
  );
});

const porcentajeValido = computed(() => {
  if (!debeValidarPorcentaje.value) return true;
  return Math.abs(diferenciaPorcentaje.value) <= TOLERANCIA_PORCENTAJE;
});

const porcentajeExcedido = computed(() => {
  if (!debeValidarPorcentaje.value) return false;
  return diferenciaPorcentaje.value < -TOLERANCIA_PORCENTAJE;
});

const porcentajeIncompleto = computed(() => {
  if (!debeValidarPorcentaje.value) return false;
  return diferenciaPorcentaje.value > TOLERANCIA_PORCENTAJE;
});
const emit = defineEmits(['update:modelValue']);

const loadProcessList = () => {
  elemento_tipo_service
    .getProcesosSeccionByElementoTipoSeccion(
      props.elementoTipo,
      props.modelValue.Codigo,
      currentModule,
    )
    .then((data) => {
      procesos.value = data.procesos || [];
    });
};

const handleAddProceso = () => {
  // Si no hay procesos cargados cargarlos desde la api
  if (procesos.value.length === 0 && props.modelValue && props.modelValue.Codigo) {
    loadProcessList();
  }

  const nuevoProcesoElemento = {
    // COTI-642: identidad cliente estable desde la creación (el selector de precedentes y el
    // diagrama la necesitan; no depender del watch asíncrono que la asigna después).
    _uid: generarUidProceso(),
    Codigo: null,
    CodigoSeccion: props.modelValue.Codigo,
    CodigoProceso: null,
    ItemProceso: null,
    CodigoRecurso: null,
    CodigoMateriaPrimaFamilia: null,
    Porcentaje: null,
    Observaciones: null,
    Proceso: null,
    Recurso: null,
    MateriaPrimaFamilia: null,
    Forma: null,
    Velocidad: null,
    VelocidadManual: null,
    Cantidad: null,
    Costo: null,
    Adicional: false,
    PorcentajeMerma: 0,
    CodigoSentidoBobinado: null,
    Material: null,
    Materiales: [],
    BocasAlto: null,
    BocasAncho: null,
    BocasAnchoFinales: null,
    DimensionAncho: null,
    CantidadColoresFrente: 0,
    CantidadColoresLiner: 0,
    CantidadColoresAdhesivo: 0,
    CantidadCambios: 0,
    FueraDeLinea: false,
    AtributosAdicionales: {},
    CategoriasMaterial: [], // Inicializar array vacío para categorías
    ElementoMateriales: [], // Inicializar array vacío para materiales
  };

  // Crear una copia del modelValue con el nuevo proceso agregado
  const updatedModelValue = {
    ...props.modelValue,
    ElementoProceso: [...props.modelValue.ElementoProceso, nuevoProcesoElemento],
  };

  emit('update:modelValue', updatedModelValue);
};

const handleUpdateProceso = (index, updatedProceso) => {
  const updatedProcesos = [...props.modelValue.ElementoProceso];
  updatedProcesos[index] = { ...updatedProceso };

  const updatedModelValue = {
    ...props.modelValue,
    ElementoProceso: updatedProcesos,
  };

  emit('update:modelValue', updatedModelValue);
};

const handleDeleteProceso = (index, e) => {
  const updatedProcesos = [...props.modelValue.ElementoProceso];
  updatedProcesos.splice(index, 1);
  const updatedModelValue = {
    ...props.modelValue,
    ElementoProceso: updatedProcesos,
  };

  emit('update:modelValue', updatedModelValue);
};

const handleDragStart = (data) => {
  draggedIndex.value = data.index;
  draggedZoneId.value = props.modelValue.Codigo;
  isDraggingGlobal.value = true;
};

const handleDragEnd = () => {
  draggedIndex.value = null;
  dragOverIndex.value = null;
  draggedZoneId.value = null;
  // Delay para asegurar que todos los watchers se ejecuten primero
  setTimeout(() => {
    isDraggingGlobal.value = false;
  }, 150);
};

const handleDragOver = (event) => {
  event.preventDefault();
  event.dataTransfer.dropEffect = 'move';
};

const handleDragEnter = (event) => {
  event.preventDefault();

  if (draggedZoneId.value !== props.modelValue.Codigo) {
    return;
  }

  const target = event.target.closest('.draggable-item');
  if (target) {
    const index = parseInt(target.dataset.index);
    dragOverIndex.value = index;
  } else {
    const rect = event.currentTarget.getBoundingClientRect();
    const y = event.clientY - rect.top;
    const itemHeight = 100;

    const calculatedIndex = Math.floor(y / itemHeight);
    const maxIndex = props.modelValue.ElementoProceso.length;

    if (calculatedIndex >= maxIndex) {
      dragOverIndex.value = maxIndex;
    } else {
      dragOverIndex.value = calculatedIndex;
    }
  }
};

const handleDragLeave = (event) => {
  if (!event.currentTarget.contains(event.relatedTarget)) {
    dragOverIndex.value = null;
  }
};

const handleDrop = (event) => {
  event.preventDefault();

  if (draggedZoneId.value !== props.modelValue.Codigo) {
    return;
  }

  const fromIndex = parseInt(event.dataTransfer.getData('text/plain'));
  const toIndex = dragOverIndex.value;

  if (fromIndex !== toIndex && toIndex !== null) {
    reorderProcesos(fromIndex, toIndex);
  }

  draggedIndex.value = null;
  dragOverIndex.value = null;
  draggedZoneId.value = null;
};

const reorderProcesos = (fromIndex, toIndex) => {
  // Hacer una copia profunda de todo el array para preservar estructuras anidadas
  const updatedProcesos = props.modelValue.ElementoProceso.map((proceso) => {
    // Usar structuredClone o JSON para copia profunda
    try {
      return structuredClone ? structuredClone(proceso) : JSON.parse(JSON.stringify(proceso));
    } catch (e) {
      // Fallback a copia manual profunda
      return JSON.parse(JSON.stringify(proceso));
    }
  });

  const [movedItem] = updatedProcesos.splice(fromIndex, 1);
  updatedProcesos.splice(toIndex, 0, movedItem);

  const updatedModelValue = {
    ...props.modelValue,
    ElementoProceso: updatedProcesos,
  };

  emit('update:modelValue', updatedModelValue);
};

onMounted(() => {
  // Obtener listado de procesos si hay al menos un proceso en el modelo
  if (!props.modelValue || !props.modelValue.Codigo) return;
  loadProcessList();
});

// Fallback: si dragend no disparó (clic rápido accidental), mouseup lo resetea
const resetDragStateFallback = () => {
  if (draggedIndex.value !== null) {
    draggedIndex.value = null;
    dragOverIndex.value = null;
    draggedZoneId.value = null;
    isDraggingGlobal.value = false;
  }
};

onMounted(() => {
  document.addEventListener('mouseup', resetDragStateFallback);
});

onUnmounted(() => {
  document.removeEventListener('mouseup', resetDragStateFallback);
});
</script>

<style scoped>
.button-position {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* Centra verticalmente los botones */
  margin-top: 20px;
  /* Agrega espacio entre los elementos */
}

.add-button {
  margin-left: auto;
  /* Mueve el botón "Agregar producto" al lado derecho */
}

/* Estilo para procesos con error de porcentaje */
.error-porcentaje-proceso {
  border-left: 3px solid #f56c6c;
  background-color: #fef0f0;
  margin-bottom: 10px;
  padding: 10px;
  border-radius: 4px;
}

.error-message {
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
  border-radius: 4px;
  padding: 8px 12px;
}

/* Ajustes responsivos */
@media (max-width: 768px) {
  .scrollable-container {
    max-height: 50vh;
    /* Ajusta la altura máxima en dispositivos más pequeños */
  }
}
.add-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.add-big {
  width: clamp(240px, 40vw, 520px);
  padding-left: 20px;
  padding-right: 20px;
}

.add-button {
  margin-left: 0 !important;
}

.drop-zone {
  min-height: 50px;
  transition: background-color 0.2s ease;
}

.drop-zone.drag-over {
  background-color: #f0f9ff;
  border: 2px dashed #3b82f6;
  border-radius: 8px;
}

.drop-zone.invalid-drop {
  background-color: #fef2f2;
  border: 2px dashed #ef4444;
  border-radius: 8px;
}

.drop-zone.invalid-drop::before {
  content: 'Solo se puede reordenar dentro de la misma sección';
  display: block;
  text-align: center;
  color: #ef4444;
  font-size: 12px;
  padding: 8px;
  background-color: #fef2f2;
  border-radius: 4px;
  margin: 4px 0;
}

.draggable-item.dragging {
  opacity: 0.5;
  transform: rotate(2deg);
  z-index: 1000;
}

.draggable-item.drag-over {
  border-top: 9px solid #3b82f666;
  background-color: #f0f9ff;
}
</style>
